import {
  createContext,
  ReactNode,
  useCallback,
  useEffect,
  useState,
} from 'react'
import { createPortal } from 'react-dom'
import ToastItem from './ToastItem'
import s from './ToastQueueProvider.module.scss'

export interface Toast {
  id: number
  type: string
  message: string
  duration: number
}

interface ToastQueueContextType {
  enqueueToast: (type: string, message: string, delay?: number) => Promise<void>
  remove: (id: number) => void
  queue: Toast[]
}

interface ToastQueueProviderProps {
  children: ReactNode
  max?: number
}

export const ToastQueueContext = createContext<ToastQueueContextType>({
  enqueueToast: () => Promise.resolve(),
  remove: () => {},
  queue: [],
})

const { Provider } = ToastQueueContext

const MAX_SNACKS = 3
const DEFAULT_DURATION = 3000 // Set a default duration for all toasts

const ToastQueueProvider: React.FC<ToastQueueProviderProps> = ({
  children,
  max = MAX_SNACKS,
}) => {
  const [queue, setQueue] = useState<Toast[]>([])
  const [isClient, setIsClient] = useState(false)

  useEffect(() => {
    setIsClient(true)
  }, [])

  const enqueueToast = useCallback(
    (type: string, message: string, delay: number = 0): Promise<void> => {
      return new Promise((resolve) => {
        setTimeout(() => {
          const toast: Toast = {
            id: Date.now(),
            type,
            message,
            duration: DEFAULT_DURATION,
          }

          setQueue((currentQueue) => {
            if (currentQueue.length >= max) {
              return [...currentQueue.slice(1), toast]
            }
            return [...currentQueue, toast]
          })

          resolve()
        }, delay)
      })
    },
    [max],
  )

  const handleRemove = useCallback((id: number) => {
    setQueue((currentQueue) => currentQueue.filter((toast) => toast.id !== id))
  }, [])

  return (
    <Provider value={{ enqueueToast, remove: handleRemove, queue }}>
      {children}

      {isClient &&
        createPortal(
          <div className={s.root}>
            {queue.map((toast) => (
              <ToastItem
                key={toast.id}
                {...toast}
                onExpire={() => handleRemove(toast.id)}
                onRemove={() => handleRemove(toast.id)}
              />
            ))}
          </div>,
          document.body,
        )}
    </Provider>
  )
}

export default ToastQueueProvider
